/**
 * Device tagging utilities
 * Handles generation and storage of unique device identifiers
 */

import { v4 as uuidv4 } from 'uuid'
import Cookies from 'js-cookie'
import { DEVICE_TAG_COOKIE_NAME, DEVICE_TAG_COOKIE_EXPIRY_DAYS } from './constants'
import { isWebAuthnSupported, registerDevice, authenticateDevice, getDeviceInfo } from './webauthn'
import {
  getWebAuthnRegisterOptions,
  completeWebAuthnRegistration,
  getWebAuthnAuthOptions,
  completeWebAuthnAuthentication,
} from '@/services/api'

// Guard to prevent multiple simultaneous WebAuthn operations
let webAuthnInProgress = false
let webAuthnPromise: Promise<string | null> | null = null

/**
 * Generates a unique device identifier using UUID v4
 * @returns A unique device tag string
 */
export function generateDeviceTag(): string {
  return uuidv4()
}

/**
 * Check if WebAuthn should be attempted based on the current domain
 * WebAuthn RP ID must match the domain, so we only attempt it on valid domains
 */
function shouldAttemptWebAuthn(): boolean {
  const hostname = window.location.hostname

  // Allow localhost and 127.0.0.1 for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return true
  }

  // Allow selenite.africa and its subdomains
  if (hostname === 'selenite.africa' || hostname.endsWith('.selenite.africa')) {
    return true
  }

  // Allow ngrok domains for testing
  if (hostname.endsWith('.ngrok-free.app') || hostname.endsWith('.ngrok.io')) {
    return true
  }

  // Don't attempt WebAuthn on local IP addresses (192.168.x.x, 10.x.x.x, etc.)
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    console.log('[Device] Skipping WebAuthn on local IP address:', hostname)
    return false
  }

  return false
}

/**
 * Try to authenticate using WebAuthn to get device tag
 * @returns Device tag if successful, null otherwise
 */
async function tryWebAuthnAuth(): Promise<string | null> {
  if (webAuthnInProgress && webAuthnPromise) {
    console.log('[Device] WebAuthn operation already in progress, waiting for result...')
    return webAuthnPromise
  }

  webAuthnInProgress = true
  webAuthnPromise = (async () => {
    try {
      console.log('[Device] Attempting WebAuthn authentication...')

      // Get authentication options from backend
      const options = await getWebAuthnAuthOptions()
      console.log('[Device] Got auth options, challenge:', options.challenge)

      // Authenticate with WebAuthn
      const credential = await authenticateDevice(options)
      console.log('[Device] Browser authentication successful, credential ID:', credential.id)

      // Complete authentication and get device tag
      console.log('[Device] Sending credential to backend for verification...')
      const result = await completeWebAuthnAuthentication(credential, options.challenge)

      console.log('[Device] ✅ Backend verification successful, device tag:', result.deviceTag)
      return result.deviceTag
    } catch (error) {
      console.error('[Device] ❌ WebAuthn authentication failed:', error)
      if (error instanceof Error && error.message.includes('not registered')) {
        console.error('[Device] Backend says credential not registered - this is a BACKEND ISSUE')
        console.error('[Device] The credential exists on device but backend database doesn\'t have it')
        console.error('[Device] Check backend webauthn_devices table and registration completion endpoint')
      }
      return null
    } finally {
      webAuthnInProgress = false
      webAuthnPromise = null
    }
  })()

  return webAuthnPromise
}

/**
 * Register device using WebAuthn and get device tag
 * @returns Device tag
 */
async function registerWithWebAuthn(): Promise<string> {
  if (webAuthnInProgress && webAuthnPromise) {
    console.log('[Device] WebAuthn operation already in progress, waiting...')
    const result = await webAuthnPromise
    if (result) return result
    throw new Error('WebAuthn operation in progress returned no result')
  }

  webAuthnInProgress = true
  const registrationPromise = (async () => {
    try {
      console.log('[Device] Starting WebAuthn registration...')

      // Get registration options from backend
      const options = await getWebAuthnRegisterOptions()
      console.log('[Device] Got registration options, challenge:', options.challenge)

      // Register with WebAuthn
      const credential = await registerDevice(options)
      console.log('[Device] Browser registration successful, credential ID:', credential.id)

      // Complete registration and get device tag
      const deviceInfo = getDeviceInfo()
      console.log('[Device] Completing registration on backend...')
      const result = await completeWebAuthnRegistration(credential, options.challenge, deviceInfo)

      console.log('[Device] ✅ Backend saved credential, device tag:', result.deviceTag)
      console.log('[Device] ⚠️ IMPORTANT: Backend should now have credential ID in webauthn_devices table')
      return result.deviceTag
    } finally {
      webAuthnInProgress = false
      webAuthnPromise = null
    }
  })()

  return registrationPromise
}

/**
 * Gets the device tag using cookies/localStorage first, then WebAuthn as fallback
 * @returns The device tag string
 */
export async function getOrCreateDeviceTag(): Promise<string> {
  console.log('[Device] Getting or creating device tag...')

  // Method 1: Check cookies first (fastest, no prompts)
  let deviceTag = Cookies.get(DEVICE_TAG_COOKIE_NAME)
  console.log('[Device] Cookie value:', deviceTag)

  if (deviceTag) {
    console.log('[Device] ✅ Found device tag in cookie:', deviceTag)
    return deviceTag
  }

  // Method 2: Try localStorage
  try {
    deviceTag = localStorage.getItem(DEVICE_TAG_COOKIE_NAME) || undefined
    console.log('[Device] localStorage value:', deviceTag)
    if (deviceTag) {
      console.log('[Device] ✅ Found device tag in localStorage:', deviceTag)
      setDeviceTag(deviceTag) // Re-save to cookie in case it was cleared
      return deviceTag
    }
  } catch (error) {
    console.warn('[Device] localStorage not available:', error)
  }

  // Method 3: Try fingerprint-based lookup (for Safari iOS when cookies/localStorage cleared)
  const fingerprint = createDeviceFingerprint()
  console.log('[Device] Device fingerprint:', fingerprint)

  try {
    const fingerprintKey = `${DEVICE_TAG_COOKIE_NAME}_fingerprint_${fingerprint}`
    const existingTag = localStorage.getItem(fingerprintKey)
    if (existingTag) {
      console.log('[Device] ✅ Found existing tag for fingerprint:', existingTag)
      setDeviceTag(existingTag)
      return existingTag
    }
  } catch (error) {
    console.warn('[Device] Failed to check fingerprint storage:', error)
  }

  // Method 4: Create new device tag
  // IMPORTANT: WebAuthn-capable devices MUST use WebAuthn (no fallback to cookie-only)
  // Non-WebAuthn devices use UUID + cookie
  const webAuthnSupported = isWebAuthnSupported()
  const shouldAttempt = shouldAttemptWebAuthn()

  if (webAuthnSupported && shouldAttempt) {
    console.log('[Device] No stored tag found, WebAuthn is supported and required')
    console.log('[Device] Will use WebAuthn for device registration (no cookie-only fallback)')

    // Try to authenticate with existing credential first
    const webAuthnTag = await tryWebAuthnAuth()

    if (webAuthnTag) {
      // Successfully authenticated, store in cookie/localStorage for quick access next time
      console.log('[Device] ✅ WebAuthn authentication successful, recovered device tag:', webAuthnTag)
      setDeviceTag(webAuthnTag)
      return webAuthnTag
    }

    // No existing credential, need to register new device
    console.log('[Device] ⚠️ No WebAuthn credential found, will register new device')
    console.log('[Device] This means either:')
    console.log('[Device]   1. First time on this device (expected)')
    console.log('[Device]   2. Backend failed to save previous registration (check backend logs)')

    // For WebAuthn-capable devices, registration is REQUIRED
    // Do NOT fall back to cookie-only mode if registration fails
    try {
      const newTag = await registerWithWebAuthn()
      console.log('[Device] ✅ WebAuthn registration successful, device tag:', newTag)
      setDeviceTag(newTag)
      return newTag
    } catch (error) {
      console.error('[Device] ❌ WebAuthn registration failed:', error)
      console.error('[Device] This device supports WebAuthn and REQUIRES biometric registration')
      console.error('[Device] Will NOT fall back to cookie-only mode')

      // Throw error to let user retry - do NOT generate fallback UUID
      throw new Error(
        `Failed to register device with biometric authentication: ${
          error instanceof Error ? error.message : 'Unknown error'
        }. Please try again.`
      )
    }
  }

  // Method 5: Generate UUID fallback (ONLY for non-WebAuthn devices)
  if (webAuthnSupported) {
    console.log('[Device] WebAuthn supported but domain not compatible')
    console.log('[Device] Domain:', window.location.hostname)
    console.log('[Device] Falling back to UUID-based device tag')
  } else {
    console.log('[Device] WebAuthn not supported on this device/browser')
    console.log('[Device] Using UUID-based device tag')
  }

  deviceTag = generateDeviceTag()
  console.log('[Device] Generated UUID device tag:', deviceTag)

  try {
    const fingerprintKey = `${DEVICE_TAG_COOKIE_NAME}_fingerprint_${fingerprint}`
    localStorage.setItem(fingerprintKey, deviceTag)
    console.log('[Device] Stored tag with fingerprint key')
  } catch (error) {
    console.warn('[Device] Failed to store fingerprint mapping:', error)
  }

  setDeviceTag(deviceTag)
  return deviceTag
}

/**
 * Stores the device tag in cookie and localStorage
 * @param deviceTag - The device tag to store
 */
export function setDeviceTag(deviceTag: string): void {
  console.log('[Device] Setting device tag:', deviceTag)

  try {
    // Store in cookie with Safari-compatible settings
    // Use 'lax' instead of 'strict' for better Safari iOS compatibility
    // Only use secure if on HTTPS
    const isSecure = window.location.protocol === 'https:'

    const cookieOptions: Cookies.CookieAttributes = {
      expires: DEVICE_TAG_COOKIE_EXPIRY_DAYS,
      sameSite: 'lax', // Changed from 'strict' for Safari iOS compatibility
      path: '/', // Ensure cookie is available across all paths
    }

    // Only set secure flag on HTTPS
    if (isSecure) {
      cookieOptions.secure = true
    }

    Cookies.set(DEVICE_TAG_COOKIE_NAME, deviceTag, cookieOptions)
    console.log('[Device] Cookie set successfully with options:', cookieOptions)

    // Verify cookie was set
    const verifyTag = Cookies.get(DEVICE_TAG_COOKIE_NAME)
    if (verifyTag === deviceTag) {
      console.log('[Device] Cookie verified successfully')
    } else {
      console.warn('[Device] Cookie verification failed. Expected:', deviceTag, 'Got:', verifyTag)
    }
  } catch (error) {
    console.warn('[Device] Failed to set cookie:', error)
  }

  // Also store in localStorage as fallback
  try {
    localStorage.setItem(DEVICE_TAG_COOKIE_NAME, deviceTag)
    console.log('[Device] localStorage set successfully')
  } catch (error) {
    console.warn('[Device] Failed to set localStorage:', error)
  }

  // Also store with fingerprint for Safari iOS fallback
  try {
    const fingerprint = createDeviceFingerprint()
    const fingerprintKey = `${DEVICE_TAG_COOKIE_NAME}_fingerprint_${fingerprint}`
    localStorage.setItem(fingerprintKey, deviceTag)
    console.log('[Device] Fingerprint mapping stored')
  } catch (error) {
    console.warn('[Device] Failed to store fingerprint mapping:', error)
  }
}

/**
 * Creates a simple device fingerprint based on browser characteristics
 * Used as a secondary identifier if primary device tag is unavailable
 * @returns A fingerprint string
 */
export function createDeviceFingerprint(): string {
  // Use only stable characteristics (removed canvas which can be inconsistent)
  const components = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    screenWidth: screen.width,
    screenHeight: screen.height,
    colorDepth: screen.colorDepth,
    timezone: new Date().getTimezoneOffset(),
    platform: navigator.platform,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
  }

  console.log('[Device] Fingerprint components:', components)

  const fingerprint = [
    components.userAgent,
    components.language,
    components.screenWidth,
    components.screenHeight,
    components.colorDepth,
    components.timezone,
    components.platform,
    components.hardwareConcurrency,
  ].join('|')

  console.log('[Device] Fingerprint string:', fingerprint)

  // Simple hash function
  let hash = 0
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  const fingerprintHash = Math.abs(hash).toString(36)
  console.log('[Device] Fingerprint hash:', fingerprintHash)

  return fingerprintHash
}

/**
 * Debug function to show all device-related data in storage
 * Call this from console to see what's stored
 */
export function debugDeviceStorage(): void {
  console.log('=== DEVICE STORAGE DEBUG ===')

  // Check cookie
  const cookieValue = Cookies.get(DEVICE_TAG_COOKIE_NAME)
  console.log('Cookie:', cookieValue)

  // Check localStorage
  try {
    const localStorageValue = localStorage.getItem(DEVICE_TAG_COOKIE_NAME)
    console.log('localStorage (main):', localStorageValue)

    // Show all fingerprint-related keys
    console.log('All localStorage keys:')
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key) {
        const value = localStorage.getItem(key)
        console.log(`  ${key}: ${value}`)
      }
    }
  } catch (error) {
    console.error('localStorage error:', error)
  }

  // Show current fingerprint
  const currentFingerprint = createDeviceFingerprint()
  console.log('Current fingerprint:', currentFingerprint)

  console.log('========================')
}

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  (window as any).debugDeviceStorage = debugDeviceStorage
}


